import { useNavigate, Link } from "react-router-dom";
import { Brain, ArrowLeft, RefreshCw, XCircle, ShieldCheck, Mail } from "lucide-react";

function PolicyLayout({ title, icon: Icon, children }) {
  const navigate = useNavigate();
  return (
    <div className="marketing-bg min-h-screen">
      <nav className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-[#27272A]">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm font-dm-sans">
            <ArrowLeft size={16} /> Back to Home
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Brain size={16} className="text-[#00F0FF]" />
            <span className="font-outfit font-bold text-[#00F0FF] text-sm">ShansoAI<span className="text-white">Hub</span></span>
          </div>
        </div>
      </nav>
      <div className="max-w-4xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-[#00F0FF]/10 flex items-center justify-center">
            <Icon size={20} className="text-[#00F0FF]" strokeWidth={1.5} />
          </div>
          <span className="text-[#00F0FF] text-xs font-bold tracking-widest uppercase font-dm-sans">ShansoAI Hub</span>
        </div>
        <h1 className="font-outfit font-black text-4xl sm:text-5xl text-white mb-3">{title}</h1>
        <p className="text-[#A1A1AA] text-sm font-dm-sans mb-10">Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>
        <div className="space-y-6 text-[#C0C0C0] font-dm-sans leading-relaxed">{children}</div>
      </div>
      <footer className="border-t border-[#27272A] py-6 px-6 mt-10">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <p className="text-[#A1A1AA] text-xs font-dm-sans">© {new Date().getFullYear()} ShansoAI Hub. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Privacy Policy</Link>
            <Link to="/terms" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Terms</Link>
            <Link to="/refund-policy" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Refund Policy</Link>
            <Link to="/contact" className="text-[#A1A1AA] hover:text-[#00F0FF] text-xs font-dm-sans">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 p-6">
      <h2 className="font-outfit font-bold text-white text-xl mb-3">{title}</h2>
      <div className="text-[#A1A1AA] text-sm leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export default function RefundPolicy() {
  return (
    <PolicyLayout title="Refund Policy" icon={RefreshCw}>
      {/* No Refund banner */}
      <div className="bg-red-500/10 border border-red-500/30 p-5 mb-2 flex items-start gap-3" data-testid="no-refund-banner">
        <XCircle size={22} className="text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-red-300 font-outfit font-bold text-base mb-1">All sales are final. No refunds.</p>
          <p className="text-[#A1A1AA] font-dm-sans text-sm leading-relaxed">
            Because every ShansoAI purchase grants instant lifetime access to digital content
            (videos, downloadable PDFs, certificates, AI-graded feedback), we do not issue
            refunds — partial or full — after payment is completed. Please review the course
            preview, curriculum, sample certificate, and FAQs <span className="text-white font-semibold">before</span> enrolling.
          </p>
        </div>
      </div>

      <Section title="What this means">
        <ul className="list-disc list-inside space-y-2">
          <li>Once your payment is confirmed by Cashfree, your enrollment is <strong className="text-white">final</strong>.</li>
          <li>This applies to <strong className="text-white">all</strong> ShansoAI products — Quick Bites, full courses, course bundles, the Mega-Pass, Python Lab Pro, SEO Suite Pro, books, and any other paid digital download.</li>
          <li>No refunds will be issued for change of mind, lack of time to study, partial completion, change in personal circumstances, or dissatisfaction with the content style.</li>
          <li>Transaction/processing fees charged by Cashfree are non-refundable in any scenario.</li>
        </ul>
      </Section>

      <Section title="Before you buy — please verify">
        <p>Every paid course on ShansoAI gives you the following before checkout, free:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>Course title, learning objectives, prerequisites, and target audience</li>
          <li>Full module-by-module curriculum (titles, summaries, durations)</li>
          <li>A <strong className="text-white">free preview</strong> of Module 1 (video)</li>
          <li>A sample of the verified certificate you&apos;ll earn</li>
          <li>The full price in INR (GST inclusive) — no hidden fees</li>
        </ul>
        <p className="mt-3">If any of these are unclear, please <Link to="/contact" className="text-[#00F0FF] hover:underline">contact our team</Link> <strong className="text-white">before</strong> paying — not after.</p>
      </Section>

      <Section title="Exceptions (technical issues only)">
        <p>
          The only situations in which a refund <em>may</em> be considered are
          verifiable technical failures on our side that we cannot resolve:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>You were charged twice for the same purchase (duplicate Cashfree order)</li>
          <li>Payment was deducted but no enrollment was created (after 24h of waiting + ticket raised)</li>
          <li>The course content is permanently removed by ShansoAI within 30 days of your purchase and we cannot offer a replacement of equal value</li>
        </ul>
        <p className="mt-3">
          In these cases, email <a href="mailto:support@shansoai.com" className="text-[#00F0FF] hover:underline">support@shansoai.com</a> with your order ID and a screenshot of the bank/UPI debit. Verified duplicate-charge refunds are processed within 5–7 business days to your original payment method.
        </p>
      </Section>

      <Section title="Course rescheduled or cancelled by ShansoAI">
        <p>
          ShansoAI courses are pre-recorded and self-paced — there are no scheduled
          live sessions to cancel. If, in the rare event we ever discontinue a paid course you&apos;ve
          purchased, you&apos;ll be offered <strong className="text-white">free access to a course of equal or higher value</strong> in our catalog.
        </p>
      </Section>

      <Section title="Why no refunds">
        <p>
          Our paid content (videos, PDFs, AI-graded capstones, certificates) is delivered
          <strong className="text-white"> instantly and irrevocably</strong> on payment. Unlike a physical book or a seat in a
          live workshop, digital content cannot be &ldquo;returned&rdquo; — once you&apos;ve accessed it,
          you&apos;ve consumed the value. A no-refund policy keeps our prices low and our
          quality high. We invest the savings back into producing better content for you.
        </p>
        <p className="mt-2 inline-flex items-center gap-2 text-[#00F0FF] text-sm font-semibold">
          <ShieldCheck size={14} /> Lifetime access · One-time payment · No subscriptions
        </p>
      </Section>

      <Section title="Questions">
        <p className="flex items-center gap-2">
          <Mail size={14} className="text-[#00F0FF]" />
          <span>
            For any pre-purchase question, please{" "}
            <Link to="/contact" className="text-[#00F0FF] hover:underline">contact us</Link> or email{" "}
            <a href="mailto:support@shansoai.com" className="text-[#00F0FF] hover:underline">support@shansoai.com</a>.
            We typically reply within 24 hours.
          </span>
        </p>
      </Section>
    </PolicyLayout>
  );
}
